import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import {
  getNextResultIndex,
  isSearchNavigationKey,
  resolveActiveResultIndex,
} from '../utils/searchResultNavigation';

export const SEARCH_RESULT_INDEX_ATTRIBUTE = 'data-search-index';

interface UseSearchResultNavigationParams {
  /** Stable keys of the visible results, in display order. */
  resultKeys: string[];
  /** Anything besides the results that should reset the active row (query, filter). */
  resetKey: string;
  onSelect: (index: number) => void;
}

const useSearchResultNavigation = ({
  resultKeys,
  resetKey,
  onSelect,
}: UseSearchResultNavigationParams) => {
  const signature = `${resetKey}\n${resultKeys.join('\n')}`;
  const [stored, setStored] = useState({ signature, index: 0 });
  const listRef = useRef<HTMLDivElement>(null);
  const resultCount = resultKeys.length;
  const activeIndex = resolveActiveResultIndex(stored, signature, resultCount);

  useEffect(() => {
    if (activeIndex < 0) return;
    const row = listRef.current?.querySelector<HTMLElement>(
      `[${SEARCH_RESULT_INDEX_ATTRIBUTE}="${activeIndex}"]`,
    );
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, signature]);

  const setActiveIndex = useCallback(
    (index: number) => setStored({ signature, index }),
    [signature],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.nativeEvent.isComposing) return;

      if (isSearchNavigationKey(event.key)) {
        if (resultCount === 0) return;
        event.preventDefault();
        setStored({ signature, index: getNextResultIndex(activeIndex, event.key, resultCount) });
        return;
      }

      if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        onSelect(activeIndex);
      }
    },
    [activeIndex, onSelect, resultCount, signature],
  );

  return { activeIndex, listRef, onKeyDown, setActiveIndex };
};

export default useSearchResultNavigation;
